<script>
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
export default {
  components: {
    NextButton,
  },
  props: {
    name: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    displayName: {
      type: String,
      default: '',
    },
    sipAuthUser: {
      type: String,
      default: '',
    },
    sipPassword: {
      type: String,
      default: '',
    },
    sipTransport: {
      type: String,
      default: '',
    },
    sipPort: {
      type: String,
      default: '',
    },
    sipDomain: {
      type: String,
      default: '',
    },
    emailEnabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['updateUser'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      userName: this.name,
      userDisplayName: this.displayName,
      userEmail: this.email,
      userSipAuthUser: this.sipAuthUser,
      userSipPassword: this.sipPassword,
      userSipTransport: this.sipTransport,
      userSipPort: this.sipPort,
      userSipDomain: this.sipDomain,
      inputStyles: {
        borderRadius: '0.75rem',
        padding: '0.375rem 0.75rem',
        fontSize: '0.875rem',
        marginBottom: '0.125rem',
      },
    };
  },
  validations: {
    userName: {
      required,
      minLength: minLength(1),
    },
    userDisplayName: {},
    userEmail: {
      required,
      email,
    },
    userSipAuthUser: {},
    userSipPassword: {},
    userSipTransport: {},
    userSipPort: {},
    userSipDomain: {},
  },
  watch: {
    name: {
      handler(value) {
        this.userName = value;
      },
      immediate: true,
    },
    displayName: {
      handler(value) {
        this.userDisplayName = value;
      },
      immediate: true,
    },
    email: {
      handler(value) {
        this.userEmail = value;
      },
      immediate: true,
    },
    sipAuthUser: {
      handler(value) {
        this.userSipAuthUser = value;
      },
      immediate: true,
    },
    sipPassword: {
      handler(value) {
        this.userSipPassword = value;
      },
      immediate: true,
    },
    sipTransport: {
      handler(value) {
        this.userSipTransport = value;
      },
      immediate: true,
    },
    sipPort: {
      handler(value) {
        this.userSipPort = value;
      },
      immediate: true,
    },
    sipDomain: {
      handler(value) {
        this.userSipDomain = value;
      },
      immediate: true,
    },
  },
  methods: {
    async updateUser() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ERROR'));
        return;
      }
      this.$emit('updateUser', {
        name: this.userName,
        displayName: this.userDisplayName,
        email: this.userEmail,
        authuser: this.userSipAuthUser,
        secret: this.userSipPassword,
        transport: this.userSipTransport,
        port: this.userSipPort,
        domain: this.userSipDomain,
      });
    },
  },
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="updateUser('profile')">
    <woot-input
      v-model="userName"
      :styles="inputStyles"
      :class="{ error: v$.userName.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.NAME.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.NAME.PLACEHOLDER')"
      :error="`${
        v$.userName.$error ? $t('PROFILE_SETTINGS.FORM.NAME.ERROR') : ''
      }`"
      @input="v$.userName.$touch"
      @blur="v$.userName.$touch"
    />
    <woot-input
      v-model="userDisplayName"
      :styles="inputStyles"
      :class="{ error: v$.userDisplayName.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.PLACEHOLDER')"
      :error="`${
        v$.userDisplayName.$error
          ? $t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.ERROR')
          : ''
      }`"
      @input="v$.userDisplayName.$touch"
      @blur="v$.userDisplayName.$touch"
    />
    <woot-input
      v-model="userSipAuthUser"
      :styles="inputStyles"
      :class="{ error: v$.userSipAuthUser.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.SIP_AUTH_USER.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.SIP_AUTH_USER.PLACEHOLDER')"
      :error="`${
        v$.userSipAuthUser.$error
          ? $t('PROFILE_SETTINGS.FORM.SIP_AUTH_USER.ERROR')
          : ''
      }`"
      @input="v$.userSipAuthUser.$touch"
      @blur="v$.userSipAuthUser.$touch"
    />
    <woot-input
      v-model="userSipPassword"
      :styles="inputStyles"
      :class="{ error: v$.userSipPassword.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.SIP_PASSWORD.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.SIP_PASSWORD.PLACEHOLDER')"
      :error="`${
        v$.userSipPassword.$error
          ? $t('PROFILE_SETTINGS.FORM.SIP_PASSWORD.ERROR')
          : ''
      }`"
      type="password"
      @input="v$.userSipPassword.$touch"
      @blur="v$.userSipPassword.$touch"
    />
    <woot-input
      v-model="userSipDomain"
      :styles="inputStyles"
      :class="{ error: v$.userSipDomain.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.SIP_DOMAIN.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.SIP_DOMAIN.PLACEHOLDER')"
      :error="`${
        v$.userSipDomain.$error
          ? $t('PROFILE_SETTINGS.FORM.SIP_DOMAIN.ERROR')
          : ''
      }`"
      @input="v$.userSipDomain.$touch"
      @blur="v$.userSipDomain.$touch"
    />
    <woot-input
      v-model="userSipPort"
      :styles="inputStyles"
      :class="{ error: v$.userSipPort.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.SIP_PORT.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.SIP_PORT.PLACEHOLDER')"
      :error="`${
        v$.userSipPort.$error ? $t('PROFILE_SETTINGS.FORM.SIP_PORT.ERROR') : ''
      }`"
      type="number"
      @input="v$.userSipPort.$touch"
      @blur="v$.userSipPort.$touch"
    />
    <label class="input-container">
      <span>{{ $t('PROFILE_SETTINGS.FORM.SIP_TRANSPORT.LABEL') }}</span>
      <select v-model="userSipTransport" class="tone-selector">
        <option
          v-for="transport in ['udp', 'tcp']"
          :key="transport"
          :value="transport"
        >
          {{ transport.toLocaleUpperCase() }}
        </option>
      </select>
    </label>
    <woot-input
      v-if="emailEnabled"
      v-model="userEmail"
      :styles="inputStyles"
      :class="{ error: v$.userEmail.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.EMAIL.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.EMAIL.PLACEHOLDER')"
      :error="`${
        v$.userEmail.$error ? $t('PROFILE_SETTINGS.FORM.EMAIL.ERROR') : ''
      }`"
      @input="v$.userEmail.$touch"
      @blur="v$.userEmail.$touch"
    />
    <div>
      <NextButton type="submit" :label="$t('PROFILE_SETTINGS.BTN_TEXT')" />
    </div>
  </form>
</template>
